// AIGeneratorWizard.vue
<template>
  <div class="ai-wizard">
    <StepIndicator :step="step" :mode="aiMode" />

    <!-- Dynamically render step component based on mode and step -->
    <component :is="getStepComponent" v-if="getStepComponent" v-model:mainPrompt="mainPrompt"
      v-model:selectedInteraction="selectedInteraction" v-model:nbQuestions="nbQuestions"
      v-model:jsonModelText="jsonModelText" v-model:apiConfig="apiConfig" :mode="aiMode" :step="step"
      @mode-selected="aiMode = $event" @submit="handleIntegration"
      v-bind="step === 5 ? { prompt: generatedPrompt } : {}"
      v-on="step === 4 ? { updatePrompt: (val) => generatedPrompt = val } : {}" />

    <div class="text-end mt-4">
      <button v-if="step > 1" @click="prevStep" class="btn btn-secondary me-2">⬅️ Previous</button>
      <button v-if="(aiMode === 'api' && step < 5) || (aiMode !== 'api' && step < 4)" @click="nextStep"
        class="btn btn-primary">
        Next ➡️
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import { useTestStore } from '@/stores/testStore';
import { v4 as uuidv4 } from 'uuid';
import api from '@/services/axios';
import StepIndicator from './StepIndicator.vue';
import StepAIUsageChoice from './wizardSteps/StepAIUsageChoice.vue';
import StepPromptDefinition from './wizardSteps/StepPromptDefinition.vue';
import StepPromptReview from './wizardSteps/StepPromptReview.vue';
import StepJSONIntegration from './wizardSteps/StepJSONIntegration.vue';
import StepAPIConfiguration from './wizardSteps/StepAPIConfiguration.vue';

const props = defineProps({
  context: {
    type: String,
    default: 'default' // 'default' | 'conditional'
  },
  testId: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['submit', 'confirm']);

const store = useTestStore();

// Fonction UUID universelle (navigateur + serveur)
const generateSecureUUID = () => {
  // Tentative avec crypto.randomUUID (contexte sécurisé uniquement)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (e) {
      console.warn('crypto.randomUUID failed, using fallback');
    }
  }
  
  // Fallback avec crypto.getRandomValues si disponible
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    
    // Version 4 UUID format
    array[6] = (array[6] & 0x0f) | 0x40;
    array[8] = (array[8] & 0x3f) | 0x80;
    
    return Array.from(array, byte => 
      byte.toString(16).padStart(2, '0')
    ).join('').replace(
      /(.{8})(.{4})(.{4})(.{4})(.{12})/,
      '$1-$2-$3-$4-$5'
    );
  }
  
  // Fallback Math.random (moins sécurisé mais fonctionnel)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper pour créer un node conditionnel à partir d'un élément
const buildConditionalNodeFromElement = (elementId, el_Type, label) => {
  const renderType = (el_Type === 'start' || el_Type === 'question' || el_Type === 'choice' || el_Type === 'termination')
    ? el_Type
    : 'question'; // fallback

  const data = {
    elementId,
    label: label || 'Question IA',
    mode: renderType === 'question' ? 'options' : undefined,
  };

  // Si c'est un sous-type de question (ex: shortAnswer, hotSpot, etc.)
  if (renderType === 'question' && (el_Type !== 'question' && el_Type !== 'choice')) {
    data.subtype = el_Type;
  }

  return {
    id: uuidv4(),
    type: renderType,
    position: {
      x: Math.random() * 400 + 100,
      y: Math.random() * 300 + 100,
    },
    collapsed: false,
    style: { width: 280, height: 140 },
    data
  };
};

const handleIntegration = async (newItems) => {
  console.log('🤖 handleIntegration - Context:', props.context, '- Items:', newItems.length);

  // MODE CONDITIONNEL
  if (props.context === 'conditional') {
    console.log('🔄 Mode conditionnel - Création d\'éléments et nodes via API atomique');

    try {
      // ÉTAPE 1 : Créer les éléments via l'API atomique (comme CreateComponent.vue)
      console.log('📝 Étape 1 : Création atomique des éléments via POST /tests/:id/elements');

      const createdElements = [];

      // Créer chaque élément individuellement via l'API atomique
      for (const item of newItems) {
        console.log('💾 Création de l\'élément:', item.el_Type, '-', item.el_Text);

        // Préparer les données de l'élément
        const elementPayload = {
          el_Type: item.el_Type,
          el_Text: item.el_Text || 'Sans titre',
          difficulty_rating: item.difficulty_rating || 5,
          options: item.options,
          scoring: item.scoring || {},
          metadata: {
            ...(item.metadata || {}),
            source: 'AIGeneratorWizard',
            createdAt: new Date().toISOString()
          }
        };

        // Appel atomique à l'API (utilise $push MongoDB - garanti persisté)
        const response = await api.post(`/tests/${props.testId}/elements`, elementPayload);

        if (!response.data.el_ID) {
          throw new Error('el_ID absent dans la réponse API');
        }

        console.log('✅ Élément créé avec ID:', response.data.el_ID);

        // Récupérer l'élément complet depuis la réponse
        const createdElement = response.data.element || {
          ...elementPayload,
          el_ID: response.data.el_ID
        };

        createdElements.push(createdElement);

        // NOTE: On ne met PAS à jour store.testData.elements ici pour éviter
        // de déclencher des watchers qui appelleraient loadStructure() pendant la création
        // Le store sera mis à jour automatiquement lors du loadStructure() final
      }

      console.log('✅ Tous les éléments créés atomiquement:', createdElements.length);
      console.log('📊 IDs des éléments créés:', createdElements.map(el => el.el_ID));

      // ÉTAPE 2 : Créer les nodes correspondants
      console.log('🔨 Étape 2 : Création des nodes avec les elementIds validés');

      const newNodes = createdElements.map((element) => {
        return buildConditionalNodeFromElement(element.el_ID, element.el_Type, element.el_Text);
      });

      console.log('📊 Nodes créés:', newNodes.length);

      // ÉTAPE 3 : Récupérer la structure actuelle et ajouter les nouveaux nodes
      console.log('🔍 Étape 3 : Récupération de la structure conditionnelle');
      const structureResponse = await api.get(`/tests/${props.testId}/conditional-structure`);
      const currentStructure = structureResponse.data.ConditionalStructure || {};

      console.log('📊 Structure actuelle:', {
        existingNodes: currentStructure.nodes?.length || 0,
        existingEdges: currentStructure.edges?.length || 0,
        startNode: currentStructure.startNode
      });

      const updatedStructure = {
        nodes: [...(currentStructure.nodes || []), ...newNodes],
        edges: currentStructure.edges || [],
        startNode: currentStructure.startNode || null,
        terminationNodes: currentStructure.terminationNodes || [],
        viewport: currentStructure.viewport || { x: 0, y: 0, zoom: 1 }
      };

      // ÉTAPE 4 : Sauvegarder la structure avec les nouveaux nodes
      console.log('💾 Étape 4 : Sauvegarde de la structure conditionnelle...');
      console.log('📤 Structure à sauvegarder:', {
        totalNodes: updatedStructure.nodes.length,
        newNodesCount: newNodes.length,
        nodeIds: updatedStructure.nodes.map(n => n.id).slice(-10)
      });

      const saveResponse = await api.put(`/tests/${props.testId}/conditional-structure`, updatedStructure);

      console.log('✅ Structure sauvegardée, réponse:', saveResponse.data);
      console.log(`✅ ${createdElements.length} éléments et ${newNodes.length} nodes créés avec succès`);

      // ÉTAPE 5 : Notifier le parent pour rafraîchir l'affichage
      emit('confirm', {
        created: true,
        elements: createdElements,
        nodes: newNodes
      });

      // Note: On n'émet PAS 'submit' pour les tests conditionnels car les éléments
      // ont déjà été créés via l'API et les nodes ont été ajoutés à la structure.
      // Le parent (ConditionalTestEditor) va recharger la structure via loadStructure().

    } catch (error) {
      console.error('❌ Erreur création éléments conditionnels:', error);
      alert(`Erreur lors de la création: ${error.message}`);
    }
    return;
  }

  // MODE DEFAULT (tests linéaires)
  const activePageId = store.getActivePageId() || store.testData.pages[0]?.id;

  newItems.forEach((item) => {
    const newId = `el_${generateSecureUUID()}`;
    item.el_ID = newId;

    store.testData.elements.push(item);

    const page = store.testData.pages.find(p => p.id === activePageId);
    if (page) {
      page.children.push({
        id: newId,
        label: item.el_Text
          ? item.el_Text.slice(0, 50) + (item.el_Text.length > 50 ? '…' : '')
          : 'New Item'
      });
    }
  });

  const ActifElementID = store.selectedElement?.el_ID;
  if (ActifElementID) {
    store.deleteElement(ActifElementID);
  }

  // 💾 Sauvegarde dans la base
  store.saveTestDataToDatabase();

  emit('submit', newItems);
};


const step = ref(1);
const mainPrompt = ref('');
const selectedInteraction = ref('');
const nbQuestions = ref(5);
const jsonModelText = ref('');
const aiMode = ref('manual');
const apiConfig = ref({
  mode: 'local',
  name: 'LMStudio - gemma-3-27b-it',
  model: 'gemma-3-27b-it',
  url: 'http://127.0.0.1:1234/v1/chat/completions',
  maxTokens: 512,
  temperature: 0.5,
  stream: false
});

const generatedPrompt = ref('');
const nextStep = () => step.value++;
const prevStep = () => step.value--;

const getStepComponent = computed(() => {
  if (step.value === 1) return StepAIUsageChoice;

  if (aiMode.value === 'manual') {
    return {
      2: StepPromptDefinition,
      3: StepPromptReview,
      4: StepJSONIntegration
    }[step.value];
  }

  if (aiMode.value === 'api') {
    return {
      2: StepAPIConfiguration,
      3: StepPromptDefinition,
      4: StepPromptReview,
      5: StepJSONIntegration
    }[step.value];
  }

  return null;
});


</script>